import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, Paper, Typography, Alert, CircularProgress, Modal,
    Chip, Tooltip, Stack, LinearProgress, Divider
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import UploadIcon from "@mui/icons-material/Upload";
import SendIcon from "@mui/icons-material/Send";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import Fuse from "fuse.js";
import axios from "axios";

/* ─────────────────────────── API ENDPOINTS (unchanged) ─────────────────────────── */
const API_BASE = "https://figazuresql.swancreate.com/api/swa";
const API_VENDOR_PRODUCTS = `${API_BASE}/FINANCE_VendorProduct`;
const API_VENDORS = `${API_BASE}/FINANCE_Vendors`;
const API_CATEGORIES_UC = `${API_BASE}/FINANCE_Categories`;
const API_SUBCATS_UC = `${API_BASE}/FINANCE_Subcategories`;
const API_CATEGORIES_LC = `${API_BASE}/finance_categories`;      // fallback
const API_SUBCATS_LC = `${API_BASE}/finance_subcategories`;      // fallback

const AI_ENDPOINT = "https://aiendpoint.swancreate.com/api/ai/gpt";

/* ─────────────────────────── Any‑Spreadsheet Upgrades ─────────────────────────── */
const ELECTRIC_GREEN = "#39FF14";
const BATCH_SIZE = 10;
const ACCEPTED_EXTS = ["csv", "txt", "tsv", "xls", "xlsx", "xlsm"]; // note: xlsb not guaranteed in browser build

/* Currency helpers */
const money = v =>
    Number(v ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const asNum = v => {
    if (v == null || v === "") return null;
    if (typeof v === "number") return v;
    const n = Number(String(v).replace(/[^\d.\-]/g, ""));
    return Number.isFinite(n) ? n : null;
};

const cleanHeader = h => String(h || "")
    .replace(/\r?\n/g, " ")
    .replace(/[^\w\s/.\-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const normalizeName = s => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const chunk = (arr, size) => {
    const out = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
};

/* Header synonyms (expanded) – used for CSV & Excel */
const COL = {
    VendorProductNumber: [
        "supc", "vendor product #", "vendor product#", "vendor product", "vendor item #", "vendor item#",
        "item #", "item number", "product code", "product #", "sysco #", "sysco item", "sku", "vpn",
        "vendorproductnumber", "itemid", "item id", "catalog #", "catalog id"
    ],
    ProductName: [
        "bid specification", "product description", "description", "item", "item description",
        "item name", "product name", "line description", "extended description", "long description"
    ],
    CasePrice: [
        "total commercial price / case", "case price", "case cost", "bid price/case", "price per case",
        "extended price/case", "case extended", "ext price", "total price", "case total", "case$", "case amount"
    ],
    UnitPrice: [
        "commercial price/svg", "commercial price / svg", "unit price", "price/unit", "unit cost", "u price", "price ea"
    ],
    ServingsPerCase: [
        "number of servings per case", "# of servings per case", "servings per case", "servings/case", "servings"
    ],
    ManufacturerNumber: [
        "manufacturer #", "mfg #", "manufacturer number", "mfr #", "mfg", "mfr", "manufacturer item", "manufacturer code"
    ],
    UPCBarcode: ["upc", "upc code", "upc #", "upcbarcode", "barcode"],
    GTIN: ["gtin", "gtin-14", "gtin14", "gtn", "u.p.c./gtin", "upc/gtin"],
    PackNum: [
        "units per case", "case qty", "pack", "pack num", "pack size", "packsize", "pack/case", "count", "cnt", "case count"
    ],
    PackType: [
        "pack type", "size", "unit size", "uom", "unit", "unit of measure", "u/m", "item size", "package size"
    ],
    UnitofMeasureDisplay: [
        "uom display", "uom text", "measure", "unit display", "unit size display"
    ],
    ContractPrice: ["contract price", "bid price", "awarded price", "contract cost"]
};

/* simple fuzzy distance on headers using Fuse */
const ALL_TARGET_KEYS = Object.keys(COL);

/* Turn “6/5 lb” or “12x12 oz” into {packNum, packType, uom} */
function parsePackText(v) {
    const t = String(v || "").trim();
    if (!t) return { packNum: null, packType: null, uom: null };
    // 6/5 lb   |  12x12 oz  |  4-5 lb
    const m = t.match(/(\d+)\s*[\/x-]\s*(\d+(?:\.\d+)?)\s*([a-zA-Z]+|\#[a-zA-Z]+)?/);
    if (m) {
        const caseCount = Number(m[1]);
        const innerQty = Number(m[2]);
        const unit = (m[3] || "").replace(/[^a-zA-Z]/g, "").toLowerCase();
        const friendly = innerQty && unit ? `${innerQty} ${unit}` : (m[3] || "");
        return { packNum: isFinite(caseCount) ? caseCount : null, packType: friendly || t, uom: friendly || t };
    }
    // fallback: single “size” only (e.g., “1 gal” or “2.5 lb”)
    const s = t.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)$/);
    if (s) return { packNum: 1, packType: t, uom: t };
    return { packNum: null, packType: t, uom: t };
}

/* Detect first likely header row (improved) */
function detectHeaderRow(sheet2d) {
    let bestIdx = 0, bestScore = -1;
    for (let r = 0; r < Math.min(50, sheet2d.length); r++) {
        const row = (sheet2d[r] || []).map(x => cleanHeader(x));
        const score =
            (row.some(c => c.includes("supc") || c.includes("sku") || c.includes("item")) ? 2 : 0) +
            (row.some(c => c.includes("description") || c.includes("product")) ? 2 : 0) +
            (row.some(c => c.includes("upc") || c.includes("gtin")) ? 1 : 0) +
            (row.some(c => c.includes("pack") || c.includes("size") || c.includes("uom")) ? 1 : 0) +
            (row.some(c => c.includes("price") || c.includes("cost")) ? 1 : 0);
        if (score > bestScore) { bestScore = score; bestIdx = r; }
    }
    return bestIdx;
}

/* Build header→key map using synonyms + fuzzy match */
function guessColumnMap(headers) {
    const cleaned = headers.map(h => ({ h, ch: cleanHeader(h) }));
    const candidates = [];

    for (const key of ALL_TARGET_KEYS) {
        const synonyms = COL[key].map(cleanHeader);
        // fuse per target key
        const fuse = new Fuse(synonyms.map(s => ({ s })), { keys: ["s"], threshold: 0.3, includeScore: true });
        cleaned.forEach(({ h, ch }, idx) => {
            let score = -1;
            // exact or includes
            for (const syn of synonyms) {
                if (ch === syn) score = Math.max(score, 1.0);
                else if (ch.includes(syn) || syn.includes(ch)) score = Math.max(score, 0.85);
            }
            // fuzzy bump (lower Fuse score = better)
            const f = fuse.search(ch)[0];
            if (f) score = Math.max(score, 0.75 * (1 - (f.score ?? 0.0)));
            if (score >= 0.5) {
                candidates.push({ header: h, key, score, idx });
            }
        });
    }

    // greedy assign: highest score first
    candidates.sort((a, b) => b.score - a.score);
    const usedHeaders = new Set(), usedKeys = new Set();
    const map = {};
    const conf = {};

    for (const c of candidates) {
        if (usedHeaders.has(c.header) || usedKeys.has(c.key)) continue;
        usedHeaders.add(c.header);
        usedKeys.add(c.key);
        map[c.header] = c.key;
        conf[c.header] = Number((c.score * 100).toFixed(0)); // %
    }
    return { map, confidence: conf };
}

/* Mfg number split (unchanged logic with minor hardening) */
function looksLikeMfgNumber(s) {
    const t = String(s || "").trim();
    if (!t) return false;
    if (/\b\d{4,}(?:[-–]\d{2,}){1,3}\b/.test(t)) return true;
    if (/\b\d{6,}\b/.test(t)) return true;
    return false;
}
function splitVendorAndMfg(cell) {
    const raw = (cell ?? "").toString();
    if (!raw.trim()) return { vendorName: "", mfgNumber: "" };
    const lines = raw
        .replace(/\u00A0/g, " ").replace(/\r/g, "")
        .split(/\n|<br\s*\/?>/i)
        .map(s => s.replace(/\s+/g, " ").trim())
        .filter(Boolean);

    let vendor = "", mfg = "";
    for (const ln of lines) {
        if (!vendor && /[A-Za-z]/.test(ln) && !looksLikeMfgNumber(ln) && !/\b(oz|lb|lbs|case|ea)\b/i.test(ln)) vendor = ln;
        if (!mfg && looksLikeMfgNumber(ln)) mfg = ln;
    }
    if (!vendor || !mfg) {
        const one = lines.join(" ");
        const m = one.match(/^(.+?)\s+(\d{4,}(?:[-–]\d{2,}){1,3}|\d{6,})\b/);
        if (m) { vendor = vendor || m[1].trim(); mfg = m[2].trim(); }
    }
    vendor = vendor.replace(/\s+(inc\.?|llc|co\.?|corp\.?)\b/i, "").trim();
    return { vendorName: vendor, mfgNumber: mfg };
}

/* ─────────────────────────── Robust AI JSON handling ─────────────────────────── */
function extractFirstJsonArray(text) {
    const s = String(text || "");
    const start = s.indexOf("[");
    if (start === -1) throw new Error("No JSON array found in AI response.");
    let depth = 0;
    for (let i = start; i < s.length; i++) {
        const ch = s[i];
        if (ch === "[") depth++;
        else if (ch === "]") {
            depth--;
            if (depth === 0) {
                const candidate = s.slice(start, i + 1);
                return JSON.parse(candidate);
            }
        }
    }
    throw new Error("Unterminated JSON array in AI response.");
}

/* ─────────────────────────── AI: per-row enrichment ─────────────────────────── */
async function aiEnrichRowsBatched(
    batches,
    knownVendorNames = [],
    categories = [],
    subcategories = [],
    onProgress = () => { },
    onBatch = () => { }
) {
    const results = new Map();
    let processed = 0;
    const total = batches.reduce((acc, b) => acc + b.length, 0);

    // token‑friendly lists
    const catList = categories.slice(0, 300).map(c => ({ id: c.CategoryID, name: c.CategoryName }));
    const subList = subcategories.slice(0, 800).map(s => ({ id: s.SubcategoryID, name: s.SubcategoryName, categoryId: s.CategoryID }));

    for (const rows of batches) {
        const payload = rows.map(r => ({
            idx: r.__idx,
            sheet: r.__sheet,
            hintVendor: r.VendorName || "",
            hintMfg: r.ManufacturerNumber || "",
            supc: r.VendorProductNumber || "",
            desc: r.ProductName || "",
            casePrice: r.CasePrice ?? null,
            unitPrice: r.UnitPrice ?? null,
            headers: r.__headers || [],
            rowByHeader: r.__rowByHeader || {},   // header → cell value (gives the AI context)
        }));

        const prompt = [
            "You are normalizing mixed-vendor bid rows to product fields.",
            "Return an ARRAY of JSON objects, one per input row, with EXACT keys:",
            '{ "idx": <number>,',
            '  "vendorName": "<string>", "manufacturerNumber": "<string|null>", "vendorProductNumber": "<string|null>",',
            '  "upc": "<string|null>", "gtin": "<string|null>",',
            '  "packNum": <number|null>, "packType": "<string|null>", "uomDisplay": "<string|null>",',
            '  "casePrice": <number|null>, "unitPrice": <number|null>, "contractPrice": <number|null>, "servingsPerCase": <number|null>,',
            '  "categoryName": "<string|null>", "subcategoryName": "<string|null>" }',
            "",
            "- Use the header names and rowByHeader map for context (they come from the actual spreadsheet).",
            "- Prefer brand/manufacturer (e.g., Tyson, Jennie-O) as vendor if present.",
            "- If 'casePrice' is present, set unitPrice = casePrice and contractPrice = casePrice.",
            "- Normalize numbers (no $ or commas).",
            "- Choose Category/Subcategory by best fit from the lists below; if unknown, return null.",
            "",
            "Known vendors:",
            JSON.stringify(knownVendorNames.slice(0, 200)),
            "",
            "Categories:",
            JSON.stringify(catList),
            "",
            "Subcategories (with categoryId):",
            JSON.stringify(subList),
            "",
            "Rows:",
            JSON.stringify(payload)
        ].join("\n");

        const { data } = await axios.post(AI_ENDPOINT, { prompt });
        const raw = (typeof data === "string" ? data : data.response || data);
        const txt = String(raw).replace(/```json|```/g, "").trim();

        let arr;
        try {
            arr = extractFirstJsonArray(txt);
        } catch {
            arr = JSON.parse(txt);
        }

        arr.forEach(o => {
            if (o && typeof o.idx === "number") results.set(o.idx, {
                vendorName: o.vendorName || "",
                manufacturerNumber: o.manufacturerNumber ?? null,
                vendorProductNumber: o.vendorProductNumber ?? null,
                upc: o.upc ?? null,
                gtin: o.gtin ?? null,
                packNum: (o.packNum ?? null),
                packType: o.packType ?? null,
                uomDisplay: o.uomDisplay ?? null,
                casePrice: (o.casePrice ?? null) !== null ? Number(o.casePrice) : null,
                unitPrice: (o.unitPrice ?? null) !== null ? Number(o.unitPrice) : null,
                contractPrice: (o.contractPrice ?? null) !== null ? Number(o.contractPrice) : null,
                servingsPerCase: (o.servingsPerCase ?? null) !== null ? Number(o.servingsPerCase) : null,
                categoryName: o.categoryName || null,
                subcategoryName: o.subcategoryName || null
            });
        });

        processed += rows.length;
        onProgress(processed, total);
        onBatch(rows.map(r => r.__idx));
    }
    return results;
}

/* ─────────────────────────── Component ─────────────────────────── */
export default function ImportBidProducts() {
    // Masters
    const [vendors, setVendors] = useState([]);
    const [vendorCache, setVendorCache] = useState(new Map()); // normName -> record
    const [allVendorProducts, setAllVendorProducts] = useState([]);
    const [productsByVendor, setProductsByVendor] = useState(new Map());
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    // Rows & UI
    const [fileName, setFileName] = useState("");
    const [rows, setRows] = useState([]);          // parsed & AI-enriched rows
    const [preview, setPreview] = useState([]);    // matched rows with __action etc
    const [columns, setColumns] = useState([]);

    // Missing vendors
    const [missingVendors, setMissingVendors] = useState([]); // [{name, norm, count}]

    // Progress / modal
    const [loading, setLoading] = useState(false);
    const [phase, setPhase] = useState(""); // label
    const [progressCurrent, setProgressCurrent] = useState(0);
    const [progressTotal, setProgressTotal] = useState(0);
    const [error, setError] = useState("");

    // Keep a cache of parsed sheet matrices so we can reprocess if needed
    const sheetsCacheRef = useRef([]); // [{ name, headers, headerRowIdx, raw2d }]

    /* Load masters on mount */
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(API_VENDORS);
                setVendors(data || []);
                const vc = new Map();
                (data || []).forEach(v => vc.set(normalizeName(v.VendorName), v));
                setVendorCache(vc);
            } catch (e) {
                setError("Failed to load vendors: " + e.message);
            }

            try {
                const [cats, subs] = await Promise.all([
                    fetchJsonFallback([API_CATEGORIES_UC, API_CATEGORIES_LC]),
                    fetchJsonFallback([API_SUBCATS_UC, API_SUBCATS_LC])
                ]);
                setCategories(cats || []);
                setSubcategories(subs || []);
            } catch (e) {
                setError(prev => prev || ("Failed to load categories/subcategories: " + e.message));
            }
        })();
    }, []);

    /* File upload handler (now supports TSV + multi‑sheet robust parsing) */
    const handleFileInput = async (file) => {
        setError("");
        setRows([]); setPreview([]); setColumns([]); setFileName(file.name);
        setMissingVendors([]);
        setLoading(true); setPhase("Reading workbook…"); setProgressCurrent(0); setProgressTotal(0);

        try {
            const ext = (file.name.split(".").pop() || "").toLowerCase();
            if (!ACCEPTED_EXTS.includes(ext)) {
                throw new Error("Unsupported file type. Please upload CSV, TSV, TXT, XLS, XLSX, or XLSM.");
            }

            const baseRows = (ext === "csv" || ext === "txt" || ext === "tsv")
                ? await parseCSVRows(file)
                : await parseWorkbookRows(file);

            if (!baseRows.length) throw new Error("No product rows found.");

            // immediate table skeleton
            setColumns(makePreviewColumns());
            setRows(baseRows.map(r => ({ ...r, __phase: "Parsed" })));
            setPreview(baseRows.map((r, i) => toPreviewRow({ ...r, __phase: "Parsed" }, i)));

            // AI enrich rows in batches of 10 with visible progress
            setPhase("AI: Enriching rows…");
            setProgressCurrent(0); setProgressTotal(baseRows.length);

            const batches = chunk(baseRows, BATCH_SIZE);
            const knownVendorNames = vendors.map(v => v.VendorName);

            const aiMap = await aiEnrichRowsBatched(
                batches,
                knownVendorNames,
                categories,
                subcategories,
                (done, total) => { setProgressCurrent(done); setProgressTotal(total); },
                (batchIdxs) => {
                    setRows(prev => prev.map(r => batchIdxs.includes(r.__idx) ? { ...r, __phase: "AI" } : r));
                    setPreview(prev => prev.map(r => batchIdxs.includes(r.__idx) ? { ...r, __phase: "AI" } : r));
                }
            );

            // Merge AI output
            const enriched = baseRows.map((r) => {
                const ai = aiMap.get(r.__idx) || {};
                const vendorName = (r.VendorName || ai.vendorName || "").trim();
                const mfg = r.ManufacturerNumber || ai.manufacturerNumber || null;

                const casePrice = (ai.casePrice ?? r.CasePrice ?? null);
                const unitPriceAI = (ai.unitPrice ?? r.UnitPrice ?? null);
                const contractPrice = (ai.contractPrice ?? r.ContractPrice ?? null);
                const unitPrice = casePrice != null ? casePrice : unitPriceAI;
                const contract = casePrice != null ? casePrice : contractPrice;

                const catName = ai.categoryName ?? null;
                const subName = ai.subcategoryName ?? null;

                return {
                    ...r,
                    VendorName: vendorName,
                    ManufacturerNumber: mfg,
                    VendorProductNumber: ai.vendorProductNumber ?? r.VendorProductNumber ?? null,
                    UPCBarcode: ai.upc ?? r.UPCBarcode ?? null,
                    GTIN: ai.gtin ?? r.GTIN ?? null,
                    PackNum: ai.packNum ?? r.PackNum ?? null,
                    PackType: ai.packType ?? r.PackType ?? null,
                    UnitofMeasureDisplay: ai.uomDisplay ?? r.UnitofMeasureDisplay ?? r.PackType ?? null,
                    CasePrice: casePrice,
                    UnitPrice: unitPrice,
                    ContractPrice: contract,
                    ServingsPerCase: ai.servingsPerCase ?? r.ServingsPerCase ?? null,
                    CategoryName: catName,
                    SubcategoryName: subName,
                    __phase: "AI",
                };
            });

            // Resolve Category/Subcategory IDs
            const withCatIds = resolveCategories(enriched, categories, subcategories);
            setRows(withCatIds);
            setPreview(withCatIds.map((r, i) => toPreviewRow(r, i)));

            // Build list of vendors missing in DB
            setPhase("Checking vendors…");
            const miss = buildMissingVendorList(withCatIds, vendorCache);
            setMissingVendors(miss);

            // Load all products for matching
            setPhase("Loading existing products…");
            const { data: allProd } = await axios.get(API_VENDOR_PRODUCTS);
            setAllVendorProducts(allProd || []);

            // Pre‑match rows by vendor
            setPhase("Matching rows…");
            const { productsMap, matched } = matchAllRows(withCatIds, allProd, vendorCache);
            setProductsByVendor(productsMap);
            setPreview(matched);

            setPhase(""); setProgressCurrent(0); setProgressTotal(0);
        } catch (e) {
            console.error(e);
            setError(e.message || "Failed to import file.");
            setPhase(""); setProgressCurrent(0); setProgressTotal(0);
        } finally {
            setLoading(false);
        }
    };

    /* CSV / TXT / TSV */
    const parseCSVRows = (file) =>
        new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                dynamicTyping: false,     // we’ll normalize
                delimiter: "",            // auto‑detect
                complete: ({ data, errors, meta }) => {
                    if (errors?.length) return reject(new Error(errors[0].message));
                    const headers = meta?.fields || Object.keys(data[0] || {});
                    const { map } = guessColumnMap(headers);
                    resolve(normalizeRowsFromObjects(data, headers, map, "CSV"));
                },
                error: reject
            });
        });

    /* XLS/XLSX/XLSM (all tabs) */
    const parseWorkbookRows = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
                    const out = [];
                    const sheetCaches = [];

                    wb.SheetNames.forEach((name) => {
                        const ws = wb.Sheets[name];
                        if (!ws) return;
                        const raw2d = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
                        if (!raw2d.length) return;

                        const headerRowIdx = detectHeaderRow(raw2d);
                        const headers = (raw2d[headerRowIdx] || []).map(h => String(h ?? ""));
                        if (!headers.length) return;

                        // build header map
                        const { map } = guessColumnMap(headers);
                        sheetCaches.push({ name, headers, headerRowIdx, raw2d });

                        // index lookup
                        const idxLookup = Object.fromEntries(headers.map((h, i) => [cleanHeader(h), i]));
                        // convenience pick by target key
                        const pickIdx = (key) => {
                            const srcH = Object.entries(map).find(([h, k]) => k === key)?.[0];
                            if (!srcH) return undefined;
                            return idxLookup[cleanHeader(srcH)];
                        };

                        const iSUPC = pickIdx("VendorProductNumber");
                        const iDESC = pickIdx("ProductName");
                        const iCASE = pickIdx("CasePrice");
                        const iUNIT = pickIdx("UnitPrice");
                        const iSERV = pickIdx("ServingsPerCase");
                        const iMFG = pickIdx("ManufacturerNumber");
                        const iUPC = pickIdx("UPCBarcode");
                        const iGTIN = pickIdx("GTIN");
                        const iPACKN = pickIdx("PackNum");
                        const iPACKT = pickIdx("PackType");
                        const iUOM = pickIdx("UnitofMeasureDisplay");

                        for (let r = headerRowIdx + 1; r < raw2d.length; r++) {
                            const row = raw2d[r] || [];
                            const supc = iSUPC != null ? (row[iSUPC] || "").toString().trim() : "";
                            const desc = iDESC != null ? (row[iDESC] || "").toString().trim() : "";
                            const casePrice = iCASE != null ? asNum(row[iCASE]) : null;
                            const unitPrice = iUNIT != null ? asNum(row[iUNIT]) : null;
                            const servings = iSERV != null ? asNum(row[iSERV]) : null;
                            const mfgCell = iMFG != null ? row[iMFG] : "";
                            const upc = iUPC != null ? (row[iUPC] || "").toString().trim() : "";
                            const gtin = iGTIN != null ? (row[iGTIN] || "").toString().trim() : "";
                            const packN = iPACKN != null ? asNum(row[iPACKN]) : null;
                            const packTCell = iPACKT != null ? row[iPACKT] : "";
                            const uomDisp = iUOM != null ? (row[iUOM] || "").toString().trim() : "";

                            if (!supc && !desc && casePrice == null && unitPrice == null && !upc && !gtin) continue;

                            // Skip category spacer rows
                            const isSpacer = !desc && supc && supc.toUpperCase() === supc && !/\d/.test(supc);
                            if (isSpacer) continue;

                            // If we have a combined size cell, parse it
                            let packNum = packN, packType = (packTCell || "").toString();
                            if (!packNum && packType) {
                                const p = parsePackText(packType);
                                packNum = packNum ?? p.packNum;
                                packType = p.packType ?? packType;
                            }

                            // Try to split vendor + mfg number from manufacturer-ish cells if needed
                            let vendorFromCell = "", mfgFromCell = "";
                            const vendorish = [iMFG].filter(i => i != null).map(i => row[i]);
                            for (const val of vendorish) {
                                const { vendorName, mfgNumber } = splitVendorAndMfg(val);
                                if (vendorName && !vendorFromCell) vendorFromCell = vendorName;
                                if (mfgNumber && !mfgFromCell) mfgFromCell = mfgNumber;
                            }

                            const unit = (casePrice != null) ? casePrice : (unitPrice ?? (casePrice != null && servings ? casePrice / servings : null));
                            const contract = (casePrice != null) ? casePrice : null;

                            // build rowByHeader for AI context
                            const rowByHeader = Object.fromEntries(headers.slice(0, 50)
                                .map((h, i) => [h, (row[i] ?? "").toString()]));

                            out.push({
                                __idx: out.length,
                                __sheet: name,
                                __headers: headers,
                                __rowByHeader: rowByHeader,
                                __cells: headers.slice(0, 12).map(h => (row[idxLookup[cleanHeader(h)]] ?? "").toString()),

                                VendorProductNumber: supc || null,
                                ManufacturerNumber: (mfgFromCell || "").trim() || null,
                                ProductName: desc || "",
                                CasePrice: casePrice,
                                UnitPrice: unit,
                                ContractPrice: contract,
                                ServingsPerCase: servings,
                                VendorName: vendorFromCell || "",
                                UPCBarcode: upc || null,
                                GTIN: gtin || null,
                                PackNum: packNum,
                                PackType: packType || null,
                                UnitofMeasureDisplay: uomDisp || (packType || null),
                            });
                        }
                    });

                    sheetsCacheRef.current = sheetCaches;
                    resolve(out);
                } catch (err) { reject(err); }
            };
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });

    /* CSV normalization with header context */
    function normalizeRowsFromObjects(data, headers, map, sheetName) {
        if (!data?.length) return [];
        const idxFromHeader = Object.fromEntries(headers.map((h, i) => [h, i]));

        return data.map((src, i) => {
            const get = (key) => {
                const srcH = Object.entries(map).find(([h, k]) => k === key)?.[0];
                return srcH ? src[srcH] : "";
            };

            const supc = get("VendorProductNumber");
            const desc = get("ProductName");
            const caseP = asNum(get("CasePrice"));
            const unitP = asNum(get("UnitPrice"));
            const serv = asNum(get("ServingsPerCase"));
            const mfg = get("ManufacturerNumber");
            const upc = (get("UPCBarcode") || "").toString().trim();
            const gtin = (get("GTIN") || "").toString().trim();
            const pN = asNum(get("PackNum"));
            let pT = get("PackType");
            const uomD = get("UnitofMeasureDisplay");

            // Parse size text if needed
            let packNum = pN, packType = pT;
            if (!packNum && pT) {
                const p = parsePackText(pT);
                packNum = packNum ?? p.packNum;
                packType = p.packType ?? pT;
            }

            // Try split vendor/mfg from any “manufacturer-ish” field if present
            let vendorFromCell = "", mfgFromCell = "";
            for (const [h, v] of Object.entries(src)) {
                const ch = cleanHeader(h);
                if (!/manufacturer|approved|vendor|brand/.test(ch)) continue;
                const { vendorName, mfgNumber } = splitVendorAndMfg(v);
                if (vendorName && !vendorFromCell) vendorFromCell = vendorName;
                if (mfgNumber && !mfgFromCell) mfgFromCell = mfgNumber;
            }

            const unit = (caseP != null) ? caseP : unitP ?? (caseP != null && serv ? caseP / serv : null);
            const contract = (caseP != null) ? caseP : null;

            const rowByHeader = Object.fromEntries(headers.slice(0, 50).map(h => [h, (src[h] ?? "").toString()]));

            return {
                __idx: i,
                __sheet: sheetName,
                __headers: headers,
                __rowByHeader: rowByHeader,
                __cells: headers.slice(0, 12).map(h => (src[h] ?? "").toString()),

                VendorProductNumber: supc ? String(supc) : null,
                ManufacturerNumber: (mfgFromCell || mfg || "").toString() || null,
                ProductName: desc || "",
                CasePrice: caseP,
                UnitPrice: unit,
                ContractPrice: contract,
                ServingsPerCase: serv,
                VendorName: vendorFromCell || "",
                UPCBarcode: upc || null,
                GTIN: gtin || null,
                PackNum: packNum ?? null,
                PackType: packType || null,
                UnitofMeasureDisplay: uomD || (packType || null),
            };
        }).filter(r =>
            r.ProductName || r.VendorProductNumber || r.CasePrice != null || r.UnitPrice != null || r.UPCBarcode || r.GTIN
        );
    }

    /* Build missing vendor list (unchanged) */
    function buildMissingVendorList(rowsWithNames, cache) {
        const counts = new Map();
        rowsWithNames.forEach(r => {
            const nm = normalizeName(r.VendorName || "");
            if (!nm) return;
            if (!cache.has(nm)) counts.set(nm, { name: r.VendorName, norm: nm, count: (counts.get(nm)?.count || 0) + 1 });
        });
        return [...counts.values()].sort((a, b) => b.count - a.count);
    }

    /* Category resolution: names → IDs (exact, then fuzzy) */
    function resolveCategories(incoming, cats, subs) {
        const nameToCat = new Map();
        cats.forEach(c => nameToCat.set(normalizeName(c.CategoryName), c));
        const catFuse = new Fuse(cats, { keys: ["CategoryName"], threshold: 0.3 });

        const subsByCat = new Map();
        subs.forEach(s => {
            const list = subsByCat.get(s.CategoryID) || [];
            list.push(s);
            subsByCat.set(s.CategoryID, list);
        });

        return incoming.map(r => {
            let categoryName = r.CategoryName || "";
            let subcategoryName = r.SubcategoryName || "";

            // Category
            let cat = categoryName ? nameToCat.get(normalizeName(categoryName)) : null;
            if (!cat && categoryName) {
                const hit = catFuse.search(categoryName)[0]?.item;
                if (hit) cat = hit;
            }

            // Subcategory (within chosen category if possible)
            let sub = null;
            if (cat) {
                const list = subsByCat.get(cat.CategoryID) || [];
                const exact = list.find(s => normalizeName(s.SubcategoryName) === normalizeName(subcategoryName));
                if (exact) sub = exact;
                else if (subcategoryName) {
                    const f = new Fuse(list, { keys: ["SubcategoryName"], threshold: 0.3 });
                    sub = f.search(subcategoryName)[0]?.item || null;
                }
            }

            return {
                ...r,
                CategoryId: cat ? String(cat.CategoryID) : null,
                CategoryName: cat ? cat.CategoryName : (r.CategoryName || null),
                SubcategoryID: sub ? String(sub.SubcategoryID) : null,
                SubcategoryName: sub ? sub.SubcategoryName : (r.SubcategoryName || null),
            };
        });
    }

    /* Match per vendor (unchanged, with small hardening) */
    function matchAllRows(rowsWithNames, allProd, cache) {
        const productsMap = new Map();
        const matched = rowsWithNames.map((r, i) => {
            const rec = cache.get(normalizeName(r.VendorName || "")) || {};
            const vid = rec.VendorID || rec.vendorID || rec.vendorid || null;

            const vendorList = (() => {
                if (vid) {
                    if (!productsMap.has(vid)) {
                        const list = (allProd || []).filter(p =>
                            String(p.VendorId || p.vendorid || "").toLowerCase() === String(vid).toLowerCase()
                        );
                        productsMap.set(vid, list);
                    }
                    return productsMap.get(vid);
                }
                return [];
            })();

            const byUPC = new Map(), byGTIN = new Map(), byVPN = new Map();
            vendorList.forEach(p => {
                if (p.UPCBarcode) byUPC.set(String(p.UPCBarcode).trim(), p);
                if (p.GTIN) byGTIN.set(String(p.GTIN).trim(), p);
                const vpn = p.VendorProductNumber || p.ManufacturerNumber;
                if (vpn) byVPN.set(String(vpn).trim(), p);
            });
            const fuse = new Fuse(vendorList, { keys: ["ProductName", "VendorProductNumber"], threshold: 0.3 });

            let match = null, matchBy = "";
            const upc = (r.UPCBarcode || "").trim();
            const gtin = (r.GTIN || "").trim();
            const vpn = (r.VendorProductNumber || r.ManufacturerNumber || "").trim();

            if (!match && upc && byUPC.has(upc)) { match = byUPC.get(upc); matchBy = "UPC"; }
            if (!match && gtin && byGTIN.has(gtin)) { match = byGTIN.get(gtin); matchBy = "GTIN"; }
            if (!match && vpn && byVPN.has(vpn)) { match = byVPN.get(vpn); matchBy = "SUPC/MFG"; }
            if (!match && r.ProductName) {
                const res = fuse.search(r.ProductName);
                if (res?.length) { match = res[0].item; matchBy = "Name(fuzzy)"; }
            }

            return {
                id: `${r.VendorName || "vendor"}-${r.VendorProductNumber || r.ProductName || "row"}-${i}`,
                ...r,
                VendorId: vid,
                __phase: r.__phase || (match ? "Matched" : "AI"),
                __action: match ? "UPDATE" : "INSERT",
                __matchBy: matchBy,
                __existingApiId: match?.apiid || null,
                __oldCase: asNum(match?.CasePrice),
                __oldUnit: asNum(match?.UnitPrice),
                __oldContract: asNum(match?.ContractPrice),
            };
        });

        return { productsMap, matched };
    }

    function toPreviewRow(r, i) {
        return {
            id: `${r.VendorName || "vendor"}-${r.VendorProductNumber || r.ProductName || "row"}-${i}`,
            ...r,
            __action: r.__action || "INSERT",
            __matchBy: r.__matchBy || "",
            __oldCase: r.__oldCase ?? null,
            __oldUnit: r.__oldUnit ?? null,
            __oldContract: r.__oldContract ?? null,
        };
    }

    function makePreviewColumns() {
        return [
            { field: "__phase", headerName: "Status", width: 110, renderCell: (p) => <Chip size="small" label={p.value || ""} /> },
            {
                field: "__action",
                headerName: "Action",
                width: 96,
                renderCell: (p) => <Chip label={p.value} size="small" color={p.value === "UPDATE" ? "warning" : "success"} />
            },
            { field: "__matchBy", headerName: "Matched By", width: 120 },
            { field: "VendorName", headerName: "Vendor", width: 180 },

            { field: "VendorProductNumber", headerName: "Vendor Item # (SUPC)", width: 160 },
            { field: "ManufacturerNumber", headerName: "MFG #", width: 140 },

            // Product prominent
            {
                field: "ProductName",
                headerName: "Product",
                flex: 2.2,
                minWidth: 520,
                renderCell: (p) => (
                    <Box sx={{ whiteSpace: "normal", overflow: "visible", textOverflow: "unset", lineHeight: 1.2 }}>
                        {p.value || ""}
                    </Box>
                ),
            },

            { field: "PackNum", headerName: "Pack", width: 80 },
            { field: "PackType", headerName: "Type/Size", width: 160 },
            { field: "UnitofMeasureDisplay", headerName: "UOM", width: 100 },

            { field: "UPCBarcode", headerName: "UPC", width: 140 },
            { field: "GTIN", headerName: "GTIN", width: 160 },

            { field: "CategoryName", headerName: "Category", width: 180 },
            { field: "SubcategoryName", headerName: "Subcategory", width: 220 },

            { field: "__oldCase", headerName: "Old Case", width: 120, valueFormatter: (p) => money(p.value) },
            { field: "CasePrice", headerName: "New Case", width: 120, valueFormatter: (p) => money(p.value) },
            { field: "__oldUnit", headerName: "Old Unit", width: 120, valueFormatter: (p) => money(p.value) },
            { field: "UnitPrice", headerName: "New Unit", width: 120, valueFormatter: (p) => money(p.value) },
            { field: "__oldContract", headerName: "Old Contract", width: 130, valueFormatter: (p) => money(p.value) },
            { field: "ContractPrice", headerName: "New Contract", width: 130, valueFormatter: (p) => money(p.value) },

            { field: "__sheet", headerName: "Sheet", width: 140 },
        ];
    }

    /* -------------- EXPORT (unchanged) -------------- */
    const TABLE_COLUMNS = [
        "ProductID", "CommodityCode", "VendorId", "VendorName", "SchoolDistrictID", "DistrictName",
        "VendorProductNumber", "UPCBarcode", "GTIN", "ProductName",
        "CategoryId", "CategoryName", "SubcategoryID", "SubcategoryName",
        "UnitofMeasureID", "UnitofMeasureDisplay", "LowUnitNumber",
        "CasePrice", "UnitPrice", "PackNum", "PackType", "ProductImageUrl", "Description",
        "isActive", "CreatedOn", "CreatedBy", "UpdatedOn", "UpdatedBy", "FIGEXID",
        "StorageLocation", "StorageLocationID", "isInventory", "InventoryCategoryID", "InventoryCategoryName",
        "ProcessID", "ID", "apiid", "ContractNumber", "ContractPrice", "isBuyAmerican", "isSmartSnack",
    ];

    const schemaMapForRow = (r) => ({
        ProductID: "",
        CommodityCode: r.CommodityCode ?? "",
        VendorId: r.VendorId ?? "",
        VendorName: r.VendorName ?? "",
        SchoolDistrictID: r.SchoolDistrictID ?? "",
        DistrictName: r.DistrictName ?? "",
        VendorProductNumber: r.VendorProductNumber ?? "",
        UPCBarcode: r.UPCBarcode ?? "",
        GTIN: r.GTIN ?? "",
        ProductName: r.ProductName ?? "",
        CategoryId: r.CategoryId ?? r.CategoryID ?? "",
        CategoryName: r.CategoryName ?? "",
        SubcategoryID: r.SubcategoryID ?? "",
        SubcategoryName: r.SubcategoryName ?? "",
        UnitofMeasureID: r.UnitofMeasureID ?? "",
        UnitofMeasureDisplay: r.UnitofMeasureDisplay ?? (r.PackType ?? ""),
        LowUnitNumber: r.LowUnitNumber ?? "",
        CasePrice: r.CasePrice ?? "",
        UnitPrice: r.CasePrice ?? (r.UnitPrice ?? ""),    // mirror rule
        PackNum: r.PackNum ?? "",
        PackType: r.PackType ?? "",
        ProductImageUrl: r.ProductImageUrl ?? "",
        Description: r.Description ?? "",
        isActive: r.isActive ?? true,
        CreatedOn: r.CreatedOn ?? "",
        CreatedBy: r.CreatedBy ?? "",
        UpdatedOn: r.UpdatedOn ?? "",
        UpdatedBy: r.UpdatedBy ?? "",
        FIGEXID: r.FIGEXID ?? "",
        StorageLocation: r.StorageLocation ?? "",
        StorageLocationID: r.StorageLocationID ?? "",
        isInventory: r.isInventory ?? "",
        InventoryCategoryID: r.InventoryCategoryID ?? "",
        InventoryCategoryName: r.InventoryCategoryName ?? "",
        ProcessID: r.ProcessID ?? "",
        ID: r.ID ?? "",
        apiid: r.__existingApiId ?? r.apiid ?? "",
        ContractNumber: r.ContractNumber ?? "",
        ContractPrice: r.CasePrice ?? (r.ContractPrice ?? ""), // mirror rule
        isBuyAmerican: r.isBuyAmerican ?? "",
        isSmartSnack: r.isSmartSnack ?? "",
    });

    const exportToCSV = () => {
        if (!preview?.length) {
            setError("Nothing to export.");
            return;
        }
        const header = TABLE_COLUMNS.join(",");
        const esc = (v) => {
            if (v === null || v === undefined) return "";
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = preview.map((r) => {
            const mapped = schemaMapForRow(r);
            return TABLE_COLUMNS.map((col) => {
                if (["CasePrice", "UnitPrice", "ContractPrice"].includes(col)) {
                    const v = mapped[col];
                    if (v === null || v === undefined || v === "") return "";
                    return String(v).replace(/[^0-9.\-]/g, "");
                }
                if (/(On)$/.test(col) && mapped[col]) {
                    try {
                        const d = new Date(mapped[col]);
                        if (!isNaN(d.getTime())) return d.toISOString();
                    } catch { /* ignore */ }
                }
                return esc(mapped[col]);
            }).join(",");
        });

        const csv = [header, ...lines].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "FINANCE_VendorProduct.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    /* Insert missing vendors (unchanged) */
    const insertMissingVendors = async () => {
        if (!missingVendors.length) return;

        setLoading(true);
        setPhase("Creating vendors…");
        setProgressCurrent(0); setProgressTotal(missingVendors.length);

        const newCache = new Map(vendorCache);
        const failures = [];

        for (let i = 0; i < missingVendors.length; i++) {
            const mv = missingVendors[i];
            const payload = {
                VendorName: mv.name || "Unknown Vendor",
                isActive: true,
                CreatedOn: new Date().toISOString(),
            };
            try {
                const { data } = await axios.post(API_VENDORS, payload);
                newCache.set(mv.norm, data);
                setVendors(prev => [...prev, data]);
            } catch (e) {
                console.error("Create vendor failed:", e);
                failures.push(mv.name);
            } finally {
                setProgressCurrent(i + 1);
            }
        }

        setVendorCache(newCache);
        setMissingVendors(failures.length ? missingVendors.filter(v => failures.includes(v.name)) : []);

        setPhase("Refreshing preview…");
        const { productsMap, matched } = matchAllRows(rows, allVendorProducts, newCache);
        setProductsByVendor(productsMap);
        setPreview(matched);

        setPhase(""); setProgressCurrent(0); setProgressTotal(0);
        setLoading(false);

        if (failures.length) setError(`${failures.length} vendor(s) failed to insert: ${failures.join(", ")}`);
    };

    /* Apply changes (unchanged) */
    const applyChanges = async () => {
        if (!preview.length) { setError("Nothing to submit."); return; }
        if (missingVendors.length) { setError("Insert new vendors before applying product updates."); return; }

        setLoading(true); setPhase("Applying product changes…");
        setProgressCurrent(0); setProgressTotal(preview.length);
        const failures = [];
        let done = 0;

        const POOL = 6;
        let idx = 0;

        const runOne = async () => {
            const i = idx++;
            if (i >= preview.length) return;
            const row = preview[i];
            const caseP = row.CasePrice != null ? Number(row.CasePrice) : null;

            const payload = {
                VendorId: row.VendorId || null,
                VendorName: row.VendorName || "",
                VendorProductNumber: row.VendorProductNumber || row.ManufacturerNumber || null,
                ManufacturerNumber: row.ManufacturerNumber || null,
                UPCBarcode: row.UPCBarcode || null,
                GTIN: row.GTIN || null,
                ProductName: row.ProductName || "",
                UnitofMeasureDisplay: row.UnitofMeasureDisplay || row.PackType || null,
                PackNum: row.PackNum != null ? Number(row.PackNum) : null,
                PackType: row.PackType || null,
                CasePrice: caseP,
                UnitPrice: caseP ?? (row.UnitPrice != null ? Number(row.UnitPrice) : null),
                ContractNumber: row.ContractNumber || null,
                ContractPrice: caseP ?? (row.ContractPrice != null ? Number(row.ContractPrice) : null),
                CategoryId: row.CategoryId || row.CategoryID || null,
                CategoryName: row.CategoryName || null,
                SubcategoryID: row.SubcategoryID || null,
                SubcategoryName: row.SubcategoryName || null,
                isActive: true,
                UpdatedOn: new Date().toISOString(),
            };

            try {
                if (row.__action === "UPDATE" && row.__existingApiId) {
                    await axios.patch(`${API_VENDOR_PRODUCTS}/${row.__existingApiId}`, payload);
                } else {
                    await axios.post(API_VENDOR_PRODUCTS, { ...payload, CreatedOn: new Date().toISOString() });
                }
            } catch (e) {
                failures.push(`${row.__action} ${row.ProductName}: ${e?.response?.statusText || e.message}`);
            } finally {
                done += 1;
                setProgressCurrent(done);
            }

            return runOne();
        };

        await Promise.all(Array(Math.min(POOL, preview.length)).fill(0).map(runOne));

        setLoading(false); setPhase(""); setProgressCurrent(0); setProgressTotal(0);
        if (failures.length) {
            setError(`${failures.length} row(s) failed. See console for details.`);
            console.error("Import failures:", failures);
        } else {
            alert("Bid products processed successfully!");
            setRows([]); setPreview([]); setFileName("");
        }
    };

    /* UI bits */
    const vendorCounts = useMemo(() => {
        const m = new Map();
        rows.forEach(r => {
            const key = r.VendorName || "(unknown)";
            if (!key) return;
            m.set(key, (m.get(key) || 0) + 1);
        });
        return [...m.entries()].sort((a, b) => b[1] - a[1]).slice(0, 12);
    }, [rows]);

    return (
        <Box p={3} display="flex" flexDirection="column" gap={2}>
            <Typography variant="h4" textAlign="center">Import Bid Products & Update Prices</Typography>

            {/* Intro / instructions */}
            <Box
                sx={(theme) => ({
                    p: 3,
                    borderRadius: 3,
                    background: `linear-gradient(145deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    color: "#fff",
                    boxShadow: `0 0 15px 4px ${theme.palette.secondary.main}`,
                })}
            >
                <Typography variant="h6" gutterBottom>How this importer works</Typography>
                <Typography variant="body2" component="div" sx={{ lineHeight: 1.6 }}>
                    <b>1) Upload a file.</b> Click <i>Upload CSV / XLS / XLSX</i>. We also support TXT/TSV. Every tab in Excel is ingested.<br /><br />
                    <b>2) Header-aware parsing.</b> We read your column headers (with synonyms + fuzzy match), normalize item numbers, pack/size (e.g., <code>6/5 lb</code>), and prices. AI receives the original headers and row cells for extra context.<br /><br />
                    <b>3) Vendor check.</b> If a vendor doesn’t exist in FIG, you’ll see the <i>New Vendors to Insert</i> panel—create them in one click before applying product updates.<br /><br />
                    <b>4) Auto-match existing products.</b> We match current <b>FINANCE_VendorProduct</b> by <i>UPC</i>, <i>GTIN</i>, <i>SUPC/MFG</i>, or fuzzy <i>Product Name</i>. The grid shows <i>Action</i> and <i>Matched By</i>.<br /><br />
                    <b>5) Review prices & categories.</b> Old vs New values display side‑by‑side. Categories map to IDs (exact then fuzzy).<br /><br />
                    <b>6) Apply changes.</b> Click <i>Apply to FIG</i> to <u>PATCH</u> matches or <u>POST</u> inserts. If Case Price exists, it mirrors to Unit & Contract to follow FIG rules.<br /><br />
                    <b>7) Optional CSV export.</b> Download an exact‑schema CSV for <b>FINANCE_VendorProduct</b> (numeric price columns preserved).
                </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                {vendorCounts.map(([name, count]) => (
                    <Chip key={name} label={`${name} — ${count}`} variant="outlined" />
                ))}

                {/* Upload */}
                <input
                    id="bid-file"
                    type="file"
                    accept=".csv,.txt,.tsv,.xls,.xlsx,.xlsm"
                    hidden
                    onChange={(e) => e.target.files[0] && handleFileInput(e.target.files[0])}
                />
                <label htmlFor="bid-file">
                    <Button component="span" startIcon={<UploadIcon />} variant="contained">
                        {fileName || "Upload CSV / XLS / XLSX"}
                    </Button>
                </label>
            </Stack>

            {/* New vendors list */}
            {missingVendors.length > 0 && (
                <Paper sx={{ p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                        <Typography variant="subtitle1">New Vendors to Insert</Typography>
                        <Button variant="outlined" onClick={insertMissingVendors}>
                            Insert {missingVendors.length} Vendor{missingVendors.length > 1 ? "s" : ""}
                        </Button>
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {missingVendors.map(v => (
                            <Chip key={v.norm} label={`${v.name} (${v.count})`} />
                        ))}
                    </Stack>
                </Paper>
            )}

            {/* Preview */}
            {preview.length > 0 && (
                <Paper sx={{ height: "64vh", width: "100%" }}>
                    <DataGrid
                        rows={preview}
                        columns={columns}
                        getRowId={(r) => r.id}
                        pageSize={25}
                        rowsPerPageOptions={[25, 50, 100]}
                        disableSelectionOnClick
                        getRowClassName={(p) => p.row.__action === "UPDATE" ? "updateRow" : "insertRow"}
                        sx={{
                            "& .updateRow": { background: "rgba(255,193,7,0.10)" },
                            "& .insertRow": { background: "rgba(76,175,80,0.10)" },
                        }}
                    />
                </Paper>
            )}

            {/* Submit + Export */}
            {preview.length > 0 && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title={missingVendors.length ? "Insert new vendors first" : "Apply updates/inserts"} arrow>
                        <span>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<SendIcon />}
                                disabled={loading || missingVendors.length > 0}
                                onClick={applyChanges}
                            >
                                {loading ? "Uploading…" : "Apply to FIG"}
                            </Button>
                        </span>
                    </Tooltip>

                    <Button variant="outlined" onClick={exportToCSV}>
                        Export CSV (schema)
                    </Button>
                </Stack>
            )}

            {/* Electric‑green progress modal */}
            <Modal open={loading || !!phase} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box sx={{
                    p: 3,
                    bgcolor: "background.paper",
                    color: "text.primary",
                    borderRadius: 2,
                    boxShadow: 24,
                    border: 1,
                    borderColor: "divider",
                    minWidth: 460,
                }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>{phase || "Processing…"}</Typography>

                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress
                                variant={progressTotal > 0 ? "determinate" : "indeterminate"}
                                value={progressTotal > 0 ? Math.min(100, (progressCurrent / progressTotal) * 100) : undefined}
                                sx={{
                                    height: 10,
                                    borderRadius: 6,
                                    backgroundColor: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "rgba(57,255,20,0.15)"
                                            : "rgba(57,255,20,0.25)",
                                    "& .MuiLinearProgress-bar": {
                                        backgroundColor: ELECTRIC_GREEN,
                                    },
                                }}
                            />
                        </Box>
                        <CircularProgress thickness={5} sx={{ color: ELECTRIC_GREEN }} />
                    </Stack>

                    <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                        {progressTotal > 0 ? `${progressCurrent} / ${progressTotal} rows` : "Initializing…"}
                    </Typography>
                </Box>
            </Modal>
        </Box>
    );
}

/* ─────────────────────────── Utilities ─────────────────────────── */

async function fetchJsonFallback(urls) {
    for (const u of urls) {
        try {
            const { data } = await axios.get(u);
            if (Array.isArray(data)) return data;
            if (data) return data;
        } catch { /* try next */ }
    }
    throw new Error(`All endpoints failed: ${urls.join(", ")}`);
}
